import React, { useState, useEffect } from 'react';
import { MapPin, Navigation } from 'lucide-react';

const LocationSection = ({ language }) => {
  const [userDistance, setUserDistance] = useState(null);
  const [locationError, setLocationError] = useState(null);

  const content = {
    de: {
      title: "Standort",
      subtitle: "Besuchen Sie uns in Bochum",
      address: "Hellweg 15, 44787 Bochum, Germany",
      navigationButton: "Start Navigation",
      distanceText: "Entfernung",
      locationError: "Standort nicht verfügbar"
    },
    en: {
      title: "Location", 
      subtitle: "Visit us in Bochum",
      address: "Hellweg 15, 44787 Bochum, Germany",
      navigationButton: "Start Navigation",
      distanceText: "Distance",
      locationError: "Location not available"
    }
  };

  // RWAN CUT coordinates (example coordinates for Bochum)
  const salonLat = 51.4818;
  const salonLng = 7.2162;

  useEffect(() => {
    if (navigator?.geolocation) {
      navigator?.geolocation?.getCurrentPosition(
        (position) => {
          const userLat = position?.coords?.latitude;
          const userLng = position?.coords?.longitude;
          const distance = calculateDistance(userLat, userLng, salonLat, salonLng);
          setUserDistance(distance);
        },
        (error) => {
          setLocationError(content?.[language]?.locationError);
        }
      );
    }
  }, [language]);

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Radius of Earth in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const handleNavigation = () => {
    const address = "Hellweg 15, 44787 Bochum, Germany";
    const encodedAddress = encodeURIComponent(address);
    
    // Detect device and open appropriate maps app
    const isIOS = /iPad|iPhone|iPod/?.test(navigator?.userAgent);
    const isAndroid = /Android/?.test(navigator?.userAgent);
    
    if (isIOS) {
      window.open(`https://maps.apple.com/?q=${encodedAddress}`, '_blank');
    } else if (isAndroid) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    } else {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    }
  };

  return (
    <section id="location" className="py-16 lg:py-24 bg-primary">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-secondary mb-4">
            {content?.[language]?.title}
          </h2>
          <p className="font-body text-lg text-white/80">
            {content?.[language]?.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Location Info */}
          <div className="space-y-6">
            {/* Address */}
            <div className="bg-secondary/10 rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <MapPin size={24} className="text-secondary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-heading font-bold text-xl text-secondary mb-2">
                    RWAN CUT
                  </h3>
                  <p className="font-body text-white leading-relaxed">
                    {content?.[language]?.address}
                  </p>
                </div>
              </div>
            </div>

            {/* Distance */}
            {userDistance && (
              <div className="bg-secondary/10 rounded-xl p-6">
                <div className="flex items-center space-x-4">
                  <Navigation size={24} className="text-secondary" />
                  <div>
                    <h3 className="font-body font-semibold text-white">
                      {content?.[language]?.distanceText}
                    </h3>
                    <p className="font-body text-secondary">
                      {userDistance?.toFixed(1)} km
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Button */}
            <button
              onClick={handleNavigation}
              className="w-full bg-secondary text-primary hover:bg-secondary/90 font-body font-semibold px-8 py-4 rounded-lg transition-luxury flex items-center justify-center space-x-2 shadow-luxury"
            >
              <Navigation size={20} />
              <span>{content?.[language]?.navigationButton}</span>
            </button>
          </div>

          {/* Embedded Map */}
          <div className="bg-secondary/10 rounded-xl p-4">
            <div className="aspect-video rounded-lg overflow-hidden">
              <iframe
                src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2482.4961!2d7.2162!3d51.4818!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTHCsDI4JzU0LjUiTiA3wrAxMic1OC4zIkU!5e0!3m2!1sen!2sde!4v1642584731234!5m2!1sen!2sde`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="RWAN CUT Location"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationSection;